import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../../api/client';
import { cn } from '../../utils/cn';
import {
  COUNTER_ICONS,
  columnTitle,
  layoutCodes,
  tabLabel,
  type CounterLayout,
  type StoredCounterLayout,
} from './counterTabs';

/**
 * The counter's resting state: tabs of categories a seller can open without typing a reference
 * (common parts, tyres, oils & liquids, packages…). The layout is edited on Settings → Counter
 * tabs (migration 148); until one is saved the server answers the built-in default.
 *
 * Every entry is a CATEGORY CODE, never a search term: our product names start with the brand
 * ("RIDEX 7O0013…"), so a text search for "Filtre à huile" finds almost nothing, while the
 * category holds every oil filter we stock. Names and counts come from the tree
 * (`/catalog/browse-categories`), so the labels are the catalogue's own and a code this install
 * has not synced simply does not appear.
 */

export interface BrowseCategory {
  code: string;
  name: string;
  count: number;
}

const TAB_KEY = 'mecadepot.counter.browseTab';

interface Props {
  onCategory: (c: BrowseCategory) => void;
  /** Rendered above the tabs (recent searches). */
  children?: ReactNode;
}

export function CounterBrowse({ onCategory, children }: Props) {
  const { t } = useTranslation();
  const [layout, setLayout] = useState<CounterLayout | null>(null);
  const [cats, setCats] = useState<Map<string, BrowseCategory> | null>(null);
  const [tab, setTab] = useState<string>(() => {
    try {
      return localStorage.getItem(TAB_KEY) ?? '';
    } catch {
      return '';
    }
  });

  useEffect(() => {
    let alive = true;
    apiClient
      .get<StoredCounterLayout>('/catalog/counter-tabs')
      .then(async (r) => {
        if (!alive) return;
        setLayout(r.data.layout);
        const codes = layoutCodes(r.data.layout);
        if (!codes.length) return setCats(new Map());
        const c = await apiClient.get<BrowseCategory[]>(
          `/catalog/browse-categories?codes=${codes.map(encodeURIComponent).join(',')}`
        );
        if (alive) setCats(new Map(c.data.map((x) => [x.code, x])));
      })
      .catch(() => {
        if (!alive) return;
        setLayout((l) => l ?? { tabs: [] });
        setCats(new Map());
      });
    return () => {
      alive = false;
    };
  }, []);

  const pick = (key: string) => {
    setTab(key);
    try {
      localStorage.setItem(TAB_KEY, key);
    } catch {
      /* not remembered — harmless */
    }
  };

  if (!layout) {
    return (
      <div className="browse">
        {children}
        <p className="bnote">{t('counter.browseTabs.loading')}</p>
      </div>
    );
  }
  const current = layout.tabs.find((x) => x.id === tab) ?? layout.tabs[0];

  // Resolve each column to what this install actually has.
  const columns = (current?.columns ?? [])
    .map((col, i) => ({
      key: `${i}`,
      title: columnTitle(t, col),
      items: col.codes.flatMap((code) => {
        const c = cats?.get(code);
        return c ? [{ id: c.code, name: c.name, count: c.count, go: () => onCategory(c) }] : [];
      }),
    }))
    .filter((col) => col.items.length > 0);
  const nothing = columns.every((col) => col.items.every((i) => i.count === 0));

  return (
    <div className="browse">
      {children}

      {layout.tabs.length > 0 && (
        <div className="btabs" role="tablist" aria-label={t('counter.browseTabs.label')}>
          {layout.tabs.map((x) => {
            const Icon = COUNTER_ICONS[x.icon] ?? COUNTER_ICONS.list;
            return (
              <button
                key={x.id}
                type="button"
                role="tab"
                aria-selected={x.id === current?.id}
                className={cn(x.id === current?.id && 'on')}
                onClick={() => pick(x.id)}
              >
                <Icon size={17} strokeWidth={1.7} />
                {tabLabel(t, x)}
              </button>
            );
          })}
        </div>
      )}

      {current && (
        <div role="tabpanel">
          {cats === null && current.columns.length > 0 ? (
            <p className="bnote">{t('counter.browseTabs.loading')}</p>
          ) : nothing ? (
            <p className="bnote">
              {current.labelKey === 'packages' && current.columns.length === 0
                ? t('counter.browseTabs.noPackages')
                : t('counter.browseTabs.emptyTab')}
            </p>
          ) : (
            <div className="bcols">
              {columns.map((col) => (
                <div key={col.key}>
                  {col.title && <h4>{col.title}</h4>}
                  {col.items.map((i) => (
                    <button
                      key={i.id}
                      type="button"
                      className="bitem"
                      onClick={i.go}
                      disabled={i.count === 0}
                    >
                      <span className="bname">{i.name}</span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
